import { useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useDemoState } from "@/data/demoState";
import { milestoneStages } from "@/data/workspaceData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Clock, Upload, Info, ArrowRight,
  Users, Send, Copy, Bell, MessageSquare, FileText, Lock, ExternalLink, Sparkles
} from "lucide-react";

export default function StageConditions() {
  const { conditions, activeProject } = useDemoState();
  const [activeStage, setActiveStage] = useState("Pre-Slab");
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);

  const projectConditions = conditions.filter((c) => c.projectId === activeProject);
  const stageConditions = projectConditions.filter((c) => c.stage === activeStage);
  const stageSummary = milestoneStages.map((stage) => {
    const sc = projectConditions.filter((c) => c.stage === stage);
    const cleared = sc.filter((c) => c.status === "cleared").length;
    const blockers = sc.filter((c) => c.isBlocker && c.status !== "cleared").length;
    return { stage, total: sc.length, cleared, blockers, percent: sc.length ? Math.round((cleared / sc.length) * 100) : 0 };
  });

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="stage-conditions-page">
        {/* Page explainers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Shows every condition from the approval decision, grouped by build stage. See what proof is missing and what is blocking your next milestone.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Clearing conditions unlocks stage readiness. You can draft requests, send upload links, and track who is holding things up.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Builder PM, permit admin, office admin. Surveyors see this in their review packs.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Project context */}
        <Card className="border-slate-200">
          <CardContent className="py-4 px-5 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold text-base text-slate-900">12 Smith St Residence</h3>
              <p className="text-xs text-slate-400">WCC-2024-0891 - Wyndham Council</p>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status="at-risk" />
              <span className="text-sm font-semibold text-slate-900">{projectConditions.filter((c) => c.status === "cleared").length}/{projectConditions.length} cleared</span>
            </div>
          </CardContent>
        </Card>

        {/* Stage overview cards */}
        <div className="grid grid-cols-5 gap-3" data-testid="stage-overview">
          {stageSummary.map((s) => (
            <Card key={s.stage} className={`border cursor-pointer transition-all hover:shadow-md ${activeStage === s.stage ? "border-blue-300 ring-2 ring-blue-100 shadow-md" : "border-slate-200"} ${s.blockers > 0 ? "bg-red-50/30" : ""}`} onClick={() => setActiveStage(s.stage)} data-testid={`stage-card-${s.stage.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="py-3 px-3 text-center">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{s.stage}</p>
                <div className="flex items-center justify-center gap-1 mb-2">
                  {s.blockers > 0 ? <AlertTriangle className="w-4 h-4 text-red-500" /> : s.percent === 100 ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-blue-500" />}
                  <span className="text-lg font-heading font-bold text-slate-900">{s.cleared}/{s.total}</span>
                </div>
                <Progress value={s.percent} className={`h-1.5 ${s.blockers > 0 ? "[&>div]:bg-red-500" : ""}`} />
                {s.blockers > 0 && <p className="text-[10px] text-red-600 font-medium mt-1.5">{s.blockers} blocker{s.blockers > 1 ? "s" : ""}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Microcopy explanations */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { text: "Blocker means this item is holding up the next stage.", icon: AlertTriangle, color: "text-red-600 bg-red-50 border-red-100" },
            { text: "Needs review means proof arrived but has not been approved by a human yet.", icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-100" },
            { text: "Proof is stored against the exact condition for audit, handover, and later requests.", icon: FileText, color: "text-blue-600 bg-blue-50 border-blue-100" },
          ].map((tip, i) => (
            <div key={i} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${tip.color}`}>
              <tip.icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <p className="text-xs font-medium leading-snug">{tip.text}</p>
            </div>
          ))}
        </div>

        {/* Conditions table */}
        <Card className="border-slate-200" data-testid="conditions-table">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" />{activeStage} Conditions</CardTitle>
              <span className="text-xs text-slate-400">{stageConditions.length} conditions</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50/50 border-b border-slate-100">
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">#</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Condition</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Plain English</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Owner</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Required Proof</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Due</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Gate</th>
                  </tr>
                </thead>
                <tbody>
                  {stageConditions.map((cond) => (
                    <tr key={cond.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer ${cond.status === "blocker" ? "bg-red-50/30" : cond.status === "needs-review" ? "bg-amber-50/20" : ""}`} onClick={() => setSelectedCondition(cond)} data-testid={`condition-row-${cond.id}`}>
                      <td className="py-3 px-4 font-mono text-xs text-slate-400">{cond.number}</td>
                      <td className="py-3 px-4 font-medium text-slate-900 text-sm">{cond.title}</td>
                      <td className="py-3 px-4 text-xs text-slate-500 max-w-[160px] truncate">{cond.plainEnglish}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-[10px] font-medium text-slate-600">{cond.owner.split(" ").map(n => n[0]).join("")}</div>
                          <span className="text-xs text-slate-600">{cond.owner}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500">{cond.evidenceType}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">{cond.dueDate}</td>
                      <td className="py-3 px-4"><StatusBadge status={cond.status} size="sm" /></td>
                      <td className="py-3 px-4">{cond.isBlocker ? <Lock className="w-3.5 h-3.5 text-red-500" /> : <span className="text-xs text-slate-300">-</span>}</td>
                    </tr>
                  ))}
                  {stageConditions.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-sm text-slate-400">No conditions for this stage yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Condition Detail Sheet */}
        <Sheet open={!!selectedCondition && !draftOpen} onOpenChange={() => setSelectedCondition(null)}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="condition-detail-sheet">
            {selectedCondition && (
              <>
                <SheetHeader>
                  <div className="flex items-center gap-2 mb-1"><Badge variant="outline" className="font-mono text-[10px]">{selectedCondition.number}</Badge><StatusBadge status={selectedCondition.status} />{selectedCondition.isBlocker && <StatusBadge status="blocker" />}</div>
                  <SheetTitle className="font-heading text-lg">{selectedCondition.title}</SheetTitle>
                </SheetHeader>
                <div className="mt-5 space-y-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Full source text</p>
                    <p className="text-sm text-slate-700 italic leading-relaxed">"{selectedCondition.description}"</p>
                    <p className="text-[10px] text-slate-400 mt-2">{selectedCondition.source}</p>
                  </div>
                  <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4">
                    <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider mb-1">What this means in plain English</p>
                    <p className="text-sm text-blue-800">{selectedCondition.plainEnglish}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Why this affects {selectedCondition.stage}</p>
                    <p className="text-xs text-slate-600">This condition must be satisfied before the {selectedCondition.stage.toLowerCase()} milestone can proceed. {selectedCondition.isBlocker ? "It is currently a blocker." : "Clearing it contributes to stage readiness."}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Owner</p><p className="text-sm font-medium text-slate-900">{selectedCondition.owner}</p></div>
                    <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Reviewer</p><p className="text-sm font-medium text-slate-900">{selectedCondition.reviewer}</p></div>
                    <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Due Date</p><p className="text-sm font-medium text-slate-900">{selectedCondition.dueDate}</p></div>
                    <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">What proof counts</p><p className="text-xs text-slate-600">{selectedCondition.evidenceRequired}</p></div>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Activity</p>
                    {selectedCondition.activity.length > 0 ? (
                      <div className="space-y-2">{selectedCondition.activity.map((a, i) => (
                        <div key={i} className="flex items-start gap-3 text-xs"><span className="text-slate-400 shrink-0 w-20">{a.date}</span><div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${a.type === "upload" ? "bg-blue-500" : a.type === "review" ? "bg-emerald-500" : "bg-slate-400"}`} /><span className="text-slate-600">{a.action}</span></div>
                      ))}</div>
                    ) : <p className="text-xs text-slate-400 italic">No activity yet.</p>}
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500">Actions</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => setDraftOpen(true)} data-testid="draft-request-btn"><Send className="w-3 h-3" /> Draft request email</Button>
                      <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="copy-upload-link-btn"><Copy className="w-3 h-3" /> Copy upload link</Button>
                      <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="send-reminder-btn"><Bell className="w-3 h-3" /> Reminder</Button>
                      <Button size="sm" variant="ghost" className="gap-1.5 text-xs"><MessageSquare className="w-3 h-3" /> Comment</Button>
                    </div>
                  </div>
                  {selectedCondition.status === "blocker" && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                      <div><p className="text-sm font-medium text-red-700">This is a blocker</p><p className="text-xs text-red-600">Upload the required proof or contact the assigned owner to resolve. Clearing this may unlock stage readiness.</p></div>
                    </div>
                  )}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Draft Email Sheet */}
        <Sheet open={draftOpen} onOpenChange={() => setDraftOpen(false)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="draft-request-sheet">
            <SheetHeader>
              <SheetTitle className="font-heading text-lg flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-600" />Draft request email</SheetTitle>
            </SheetHeader>
            <div className="mt-5 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Suggested recipient</p>
                <p className="text-sm font-medium text-slate-900">{selectedCondition?.owner}</p>
                <p className="text-xs text-slate-400 mt-0.5">Previously handled similar items for this project.</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <pre className="text-xs text-slate-700 whitespace-pre-wrap font-body leading-relaxed">{`Dear ${selectedCondition?.owner || "Consultant"},

Re: 12 Smith St Residence - ${selectedCondition?.title || "Condition"}

We require the following to satisfy condition ${selectedCondition?.number || ""} of planning permit WCC-2024-0891:

${selectedCondition?.evidenceRequired || "Required documentation"}

Please upload using the secure link below:
[Secure Upload Link]

Due: ${selectedCondition?.dueDate || ""}

Kind regards,
Sarah Chen
Builder PM`}</pre>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800 gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy to clipboard</Button>
                <Button size="sm" variant="outline" className="gap-1.5"><ExternalLink className="w-3.5 h-3.5" /> Open in Outlook</Button>
              </div>
              <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs text-blue-700">AI drafts. Humans send. Review and send from your own email client.</p>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
